import React, { useState, useEffect } from "react";
import env from "@beam-australia/react-env";

import useApi from "../../../../hooks/useApi";
import helperFormats from "../../../../utils/helperFormats";

// ─── design tokens (Stitch palette) ──────────────────────────────────────────
const C = {
    primary: "#002542",
    secondary: "#006b5f",
    secContainer: "#8df5e4",
    onSecContainer: "#007165",
    tertiaryContainer: "#273c45",
    tertiaryFixed: "#cfe6f2",
    surface: "#f8f9fa",
    surfaceLowest: "#ffffff",
    surfaceLow: "#f3f4f5",
    outline: "#73777e",
    outlineVariant: "#c3c6ce",
    error: "#ba1a1a",
};

// ─── type metadata ────────────────────────────────────────────────────────────
const TYPE_META = {
    ALTA_DE_BENEFICIARIO: { label: "Alta de beneficiario", icon: "pi pi-user", bg: C.secondary, fg: "#fff" },
    ALTA_DE_SOLICITUD: { label: "Alta de solicitud", icon: "pi pi-file", bg: "#1b3b5a", fg: "#abc9ef" },
    ALTA_DE_RETENCION: { label: "Alta de retención", icon: "pi pi-percentage", bg: C.tertiaryContainer, fg: C.tertiaryFixed },
    CONFIRMACION_DE_RETENCION: { label: "Confirmación de retención", icon: "pi pi-check-circle", bg: C.secondary, fg: "#fff", accent: true },
    MORA: { label: "Mora", icon: "pi pi-exclamation-triangle", bg: C.error, fg: "#fff" },
    PAGO_MORA: { label: "Pago de mora", icon: "pi pi-replay", bg: "#c2410c", fg: "#fff" },
    PAGO: { label: "Pago", icon: "pi pi-dollar", bg: "#15803d", fg: "#fff" },
    CREDITO_CUENTA_CORRIENTE: { label: "Cuenta Corriente - CRÉDITO", icon: "pi pi-arrow-circle-up", bg: "#0891b2", fg: "#fff" },
    DEBITO_CUENTA_CORRIENTE: { label: "Cuenta Corriente - DÉBITO", icon: "pi pi-arrow-circle-down", bg: "#475569", fg: "#fff" },
};
const getMeta = (type) => TYPE_META[type] ?? { label: type, icon: "pi pi-circle", bg: "#94a3b8", fg: "#fff" };

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmtAmount = (v) => {
    if (v == null) return "";
    const n = parseFloat(String(v).replace(",", "."));
    if (isNaN(n)) return String(v);
    return "$ " + helperFormats.formatDecimal(n, 2, 2);
};

const fmtDate = (d) => {
    const dt = new Date(d);
    return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
        + " " + dt.toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });
};

const PRODUCT_LABEL = { "NEW_CREDIT": "NUEVO CRÉDITO", "RENEWAL": "RENOVACIÓN" };

const STATUS_BADGE = {
    APPROVED: { bg: C.secContainer, color: C.onSecContainer, label: "APROBADO" },
    REJECTED: { bg: "#ffdad6", color: "#93000a", label: "RECHAZADO" },
    PENDING: { bg: "#fef9c3", color: "#a16207", label: "PENDIENTE" },
};

const parseFields = (type, info) => {
    if (!info) return [];
    switch (type) {
        case "ALTA_DE_BENEFICIARIO": {
            const idx = info.lastIndexOf(" - ");
            return [
                { label: "NOMBRE", value: idx >= 0 ? info.substring(0, idx).trim() : info },
                { label: "DOCUMENTO", value: idx >= 0 ? info.substring(idx + 3).trim() : "" },
            ];
        }
        case "ALTA_DE_SOLICITUD": {
            const p = info.split(" - ");
            return [
                { label: "PRODUCTO", value: PRODUCT_LABEL[p[0]?.trim()] ?? p[0]?.trim() },
                { label: "MONTO", value: fmtAmount(p[1]?.trim()) },
                { label: "CUOTAS", value: p[2]?.trim() },
                { label: "VALOR CUOTA", value: fmtAmount(p[3]?.trim()) },
                { label: "RESULTADO", value: p[4]?.trim(), isBadge: true },
            ];
        }
        case "ALTA_DE_RETENCION": {
            const p = info.split(" - ");
            return [
                { label: "PERÍODO", value: p[0]?.trim() },
                { label: "MONTO GENERADO", value: fmtAmount(p[1]?.trim()) },
            ];
        }
        case "CONFIRMACION_DE_RETENCION": {
            const p = info.split(" - ");
            return [
                { label: "PERÍODO", value: p[0]?.trim() },
                { label: "SUMA CONFIRMADA", value: fmtAmount(p[1]?.trim()), highlight: true },
            ];
        }
        case "MORA":
        case "PAGO_MORA":
        case "PAGO": {
            const [sumStr, totalStr] = info.split(" = ");
            const sp = (sumStr || "").split(" + ");
            return [
                { label: "CAPITAL", value: fmtAmount(sp[0]?.trim()) },
                { label: "INTERESES", value: fmtAmount(sp[1]?.trim()) },
                { label: "IMP. INT.", value: fmtAmount(sp[2]?.trim()) },
                { label: "TOTAL", value: fmtAmount(totalStr?.trim()), highlight: true },
            ];
        }
        case "CREDITO_CUENTA_CORRIENTE":
        case "DEBITO_CUENTA_CORRIENTE": {
            const idx = info.indexOf(" - ");
            const notes = idx >= 0 ? info.substring(idx + 3).trim() : "";
            return [
                { label: "MONTO", value: fmtAmount(idx >= 0 ? info.substring(0, idx).trim() : info) },
                ...(notes ? [{ label: "DETALLE", value: notes, wide: true }] : []),
            ];
        }
        default:
            return [{ label: "INFO", value: info, wide: true }];
    }
};

// ─── sub-components ───────────────────────────────────────────────────────────
const StatusBadge = ({ value }) => {
    const s = STATUS_BADGE[value] ?? { bg: "#e2e8f0", color: "#475569", label: value };
    return (
        <span style={{ display: "inline-block", backgroundColor: s.bg, color: s.color, borderRadius: "999px", padding: "1px 10px", fontSize: "0.65rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {s.label}
        </span>
    );
};

const FieldRow = ({ fields }) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem 2rem", padding: "0.6rem 0.9rem", background: C.surfaceLow, borderRadius: "8px", marginTop: "0.6rem" }}>
        {fields.map((f, i) => (
            <div key={i} style={{ minWidth: f.wide ? "100%" : "auto" }}>
                <div style={{ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.outline, marginBottom: "2px" }}>
                    {f.label}
                </div>
                {f.isBadge
                    ? <StatusBadge value={f.value} />
                    : <div style={{ fontSize: "0.8rem", fontWeight: f.highlight ? 700 : 600, color: f.highlight ? C.primary : "#191c1d" }}>{f.value}</div>
                }
            </div>
        ))}
    </div>
);

// ─── timeline card ────────────────────────────────────────────────────────────
const TimelineCard = ({ item }) => {
    const meta = getMeta(item.type);
    const fields = parseFields(item.type, item.info);
    const isConfirm = item.type === "CONFIRMACION_DE_RETENCION";
    return (
        <div style={{
            background: C.surfaceLowest,
            borderRadius: "12px",
            padding: "0.9rem 1.1rem",
            boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
            borderLeft: isConfirm ? `4px solid ${C.secondary}` : "none",
        }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                <h3 style={{ margin: 0, fontWeight: 700, fontSize: "0.88rem", color: isConfirm ? C.secondary : C.primary }}>
                    {meta.label}
                </h3>
                <span style={{ flexShrink: 0, fontSize: "0.68rem", fontWeight: 500, color: C.outline, background: C.surfaceLow, padding: "2px 7px", borderRadius: "4px", whiteSpace: "nowrap" }}>
                    {fmtDate(item.date)}
                </span>
            </div>
            <FieldRow fields={fields} />
            {item.linkedPayments?.length > 0 && (
                <div style={{ marginTop: "0.75rem", borderTop: `1px dashed ${C.outlineVariant}`, paddingTop: "0.6rem" }}>
                    {item.linkedPayments.map((p, i) => {
                        const pmeta = getMeta(p.type);
                        return (
                            <div key={i} style={{ marginTop: i > 0 ? "0.75rem" : 0 }}>
                                <div style={{ fontSize: "0.72rem", fontWeight: 700, color: pmeta.bg, marginBottom: "2px" }}>
                                    <i className={pmeta.icon} style={{ marginRight: "5px" }} />
                                    {pmeta.label}
                                </div>
                                <FieldRow fields={parseFields(p.type, p.info)} />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── merge helper ─────────────────────────────────────────────────────────────
const mergeConfirmationsWithPayments = (rawEvents) => {
    const absorbedIndices = new Set();
    rawEvents.forEach((e, i) => {
        if (
            (e.type === "PAGO" || e.type === "PAGO_MORA") &&
            e.withholdingId != null &&
            rawEvents.some((c) => c.type === "CONFIRMACION_DE_RETENCION" && c.withholdingId === e.withholdingId)
        ) {
            absorbedIndices.add(i);
        }
    });
    return rawEvents
        .map((e) => {
            if (e.type !== "CONFIRMACION_DE_RETENCION") return e;
            const linked = rawEvents.filter(
                (p) =>
                    (p.type === "PAGO" || p.type === "PAGO_MORA") &&
                    p.withholdingId != null &&
                    p.withholdingId === e.withholdingId
            );
            return { ...e, linkedPayments: linked };
        })
        .filter((_, i) => !absorbedIndices.has(i));
};

// ─── main component ───────────────────────────────────────────────────────────
const BeneficiaryTimeline = ({ documentNumber }) => {
    const api = useApi();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!documentNumber) return;
        setLoading(true);
        setError(false);
        api.get(`${env("API_URL")}/beneficiaries/timeline/document/${documentNumber}`)
            .then((res) => setEvents(mergeConfirmationsWithPayments(res.data)))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentNumber]);

    if (loading) return <div style={{ padding: "1rem 2rem", color: C.outline }}>Cargando línea de tiempo...</div>;
    if (error) return <div style={{ padding: "1rem 2rem", color: C.error }}>Error al cargar la línea de tiempo.</div>;
    if (!events.length) return <div style={{ padding: "1rem 2rem", color: C.outline }}>Sin eventos registrados.</div>;

    return (
        <div style={{ padding: "1rem 2rem 1.5rem", background: C.surface }}>
            <div style={{ fontSize: "0.72rem", color: C.outline, marginBottom: "1rem", display: "flex", alignItems: "center", gap: "6px" }}>
                <i className="pi pi-info-circle" style={{ color: C.secondary }} />
                <span>{events.length} evento{events.length !== 1 ? "s" : ""} · documento <strong>{documentNumber}</strong></span>
            </div>
            <div style={{ position: "relative" }}>
                {/* vertical connecting line */}
                <div style={{ position: "absolute", left: "19px", top: "20px", bottom: "20px", width: "2px", background: C.outlineVariant, borderRadius: "1px" }} />
                <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
                    {events.map((item, idx) => {
                        const meta = getMeta(item.type);
                        return (
                            <div key={idx} style={{ display: "flex", gap: "1rem", alignItems: "flex-start", position: "relative" }}>
                                <div style={{ flexShrink: 0, width: "40px", height: "40px", borderRadius: "50%", background: meta.bg, color: meta.fg, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.18)", zIndex: 1, fontSize: "1rem" }}>
                                    <i className={meta.icon} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <TimelineCard item={item} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default BeneficiaryTimeline;
